import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Zap, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = location.state?.from || (u.role === "admin" ? "/admin" : "/dashboard");
      navigate(dest);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#0A0A0A]">
        <img src="https://customer-assets.emergentagent.com/job_quick-bike-share-1/artifacts/70szyjra_AFE27A29-BD00-40BF-BE38-EC289A435C8D.PNG" alt="Matte black Honda PCX" className="w-full h-full object-cover opacity-80" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-black text-2xl text-white">
            <span className="grid place-items-center w-9 h-9 rounded-md bg-primary"><Zap className="w-5 h-5" /></span> Dirt Or Road
          </Link>
          <p className="text-white text-3xl font-black tracking-tight max-w-sm">Welcome back. Your next shift is one click away.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 font-display font-black text-xl mb-8">
            <span className="grid place-items-center w-8 h-8 rounded-md bg-primary text-white"><Zap className="w-5 h-5" /></span> Dirt Or Road
          </Link>
          <h1 className="text-3xl font-black tracking-tight">Log in</h1>
          <p className="mt-2 text-muted-foreground">Manage your rentals and bookings.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5 bg-secondary/50" placeholder="you@email.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5 bg-secondary/50" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-primary hover:bg-[#1D4ED8] text-white font-bold">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            New here? <Link to="/register" className="font-bold text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
